<template>
  <teleport to="body">
    <div
      class="chatbot-assistant"
      :class="[
        `chatbot-assistant--${position}`,
        { 'chatbot-assistant--visible': isVisible },
        { 'chatbot-assistant--expanded': isExpanded },
      ]"
    >
      <!-- Chatbot Button -->
      <div
        class="chatbot-button-container"
        ref="draggableButtonRef"
        :style="dragJsStyles"
      >
        <transition name="button-fade">
          <div
            v-if="showButton"
            class="chatbot-button"
            @click="toggleChatbot"
            @keydown.enter="toggleChatbot"
            @keydown.space.prevent="toggleChatbot"
            @mouseenter="handleMouseEnter"
            @mouseleave="handleMouseLeave"
            role="button"
            tabindex="0"
            :aria-label="isExpanded ? 'Close chatbot' : 'Open chatbot'"
          >
            <div class="chatbot-button__icon">
              <img
                src="/images/maker-cart/edward-ai.png"
                alt="Chatbot AI"
                class="chatbot-button__image"
              />
            </div>
          </div>
        </transition>
        <!-- Tooltip -->
        <transition name="tooltip-fade">
          <div
            v-if="!isExpanded"
            class="chatbot-tooltip"
            :class="[
              `chatbot-tooltip--${currentSide}`,
              `chatbot-tooltip--${tooltipVertical}`,
            ]"
            v-show="showTooltipNow"
            @mouseenter="handleMouseEnter"
            @mouseleave="handleMouseLeave"
          >
            <div class="chatbot-tooltip__content">
              <p class="chatbot-tooltip__text">Hello, I'm Edward!</p>
              <p class="chatbot-tooltip__text">Ask me about the Makercart</p>
            </div>
            <div
              class="chatbot-tooltip__arrow"
              :class="`chatbot-tooltip__arrow--${currentSide}`"
            ></div>
          </div>
        </transition>
      </div>

      <!-- Chat Window -->
      <transition name="chat-expand">
        <div
          v-if="isExpanded"
          ref="chatWindowRef"
          class="chatbot-window"
          :class="{ 'chatbot-window--with-messages': messages.length > 0 }"
          :style="chatWindowStyle"
        >
          <!-- Header -->
          <div class="chatbot-header">
            <div class="chatbot-header__left">
              <img
                src="/images/maker-cart/edward-ai.png"
                alt="Edward AI"
                class="chatbot-header__avatar"
              />
              <div class="chatbot-header__info">
                <h3 class="chatbot-header__title">Chat with Edward</h3>
                <p class="chatbot-header__subtitle">
                  Hello, What can I help you with today?
                </p>
              </div>
            </div>
            <button
              type="button"
              class="chatbot-header__close"
              aria-label="Close chatbot"
              @click="toggleChatbot"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <!-- Chat Body -->
          <div class="chatbot-body">
            <!-- Sticky Suggestions (when there are messages) -->
            <transition name="suggestions-fade">
              <div
                v-if="messages.length > 0 && questions.length > 0"
                class="chatbot-suggestions chatbot-suggestions--sticky"
              >
                <h4 class="chatbot-suggestions__title">Suggestions</h4>
                <button
                  v-for="(question, index) in questions"
                  :key="index"
                  class="chatbot-suggestion"
                  :disabled="isLoading"
                  @click="sendMessage(question.question)"
                >
                  {{ question.question }}
                </button>
              </div>
            </transition>

            <!-- Messages -->
            <div
              class="chatbot-messages"
              ref="messagesContainer"
              v-if="messages.length > 0"
              @click="handleActivityLinkClick"
            >
              <transition-group name="message-slide">
                <div
                  v-for="message in messages"
                  :key="message.id"
                  class="chatbot-message"
                  :class="`chatbot-message--${message.type}`"
                >
                  <div
                    class="chatbot-message__content"
                    :class="{
                      'chatbot-message__content--html': message.type === 'bot',
                      'chatbot-message__content--streaming':
                        message.type === 'bot' && message.isStreaming,
                    }"
                  >
                    <span v-if="message.type === 'user'">{{
                      message.text
                    }}</span>
                    <div v-else>
                      <div v-html="message.html || message.text"></div>
                    </div>
                  </div>
                </div>
              </transition-group>

              <!-- Loading indicator (only show before first chunk arrives) -->
              <div
                v-if="isLoading && !hasReceivedFirstChunk"
                class="chatbot-message chatbot-message--bot"
              >
                <div class="chatbot-message__content chatbot-message__loading">
                  <div class="loading-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Initial Suggestions (when no messages) -->
            <transition name="suggestions-fade">
              <div
                v-if="messages.length === 0"
                class="chatbot-suggestions chatbot-suggestions--initial"
              >
                <div
                  v-if="isLoadingQuestions"
                  class="chatbot-suggestions__loading"
                >
                  <div class="loading-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <p class="chatbot-suggestions__loading-text">
                    Loading suggestions...
                  </p>
                </div>
                <template v-else-if="questions.length > 0">
                  <h4 class="chatbot-suggestions__title">Suggestions</h4>
                  <div class="chatbot-suggestions__buttons">
                    <button
                      v-for="(question, index) in questions"
                      :key="index"
                      class="chatbot-suggestion"
                      :disabled="isLoading"
                      @click="sendMessage(question.question)"
                    >
                      {{ question.question }}
                    </button>
                  </div>
                </template>
                <div v-else class="chatbot-suggestions__empty">
                  <p>No suggestions available</p>
                </div>
              </div>
            </transition>
          </div>

          <!-- Input Area -->
          <div class="chatbot-input">
            <div class="chatbot-input__wrapper">
              <input
                v-model="inputMessage"
                type="text"
                class="chatbot-input__field"
                placeholder="Type something do you want to ask"
                @keypress.enter="sendUserMessage"
              />
              <button
                type="button"
                class="chatbot-input__send"
                aria-label="Send message"
                @click="sendUserMessage"
              >
                <SendIcon width="24" height="24" />
              </button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </teleport>
</template>

<script setup>
/* eslint-disable */
import {
  ref,
  onMounted,
  onBeforeUnmount,
  nextTick,
  defineProps,
  watch,
  computed,
  reactive,
} from "vue";
import { SendIcon } from "@/components/icons";
import ChatbotService from "@/services/chatbotService";
import { marked } from "marked";
import { useChatStore } from "@/store/chatStore";
import { useLocaleStore } from "@/store/locale";
import { useRouter } from "vue-router";
import { useDraggable, useWindowSize } from "@vueuse/core";

// Configure marked for better security and formatting
marked.setOptions({
  breaks: true,
  gfm: true,
  sanitize: false,
  smartLists: true,
  smartypants: true,
});

const props = defineProps({
  position: {
    type: String,
    default: "right",
    validator: (value) => ["left", "right"].includes(value),
  },
  buttonBottomOffset: {
    type: Number,
    default: 0,
  },
  showTooltip: {
    type: Boolean,
    default: true,
  },
  autoShow: {
    type: Boolean,
    default: true,
  },
  delay: {
    type: Number,
    default: 2000,
  },
  tooltipDuration: {
    type: Number,
    default: 5000,
  },
  pageName: {
    type: String,
    default: "",
  },
  activityName: {
    type: String,
    default: null,
  },
});

// Initialize chat store, locale store, and router
const chatStore = useChatStore();
const localeStore = useLocaleStore();
const router = useRouter();

const isVisible = ref(false);
const isExpanded = ref(false);
const showTooltipNow = ref(false);
const isHovered = ref(false);
const tooltipTimer = ref(null);
const inputMessage = ref("");
const showButton = ref(true);
const messagesContainer = ref(null);
const questions = ref([]);
const isLoadingQuestions = ref(false);
const hasReceivedFirstChunk = ref(false);

// Draggable Logic
const draggableButtonRef = ref(null);
const { width: windowWidth, height: windowHeight } = useWindowSize();
// Initial large values to put it off-screen until mounted calculation
const isDraggingActive = ref(false);

const { x, y, isDragging } = useDraggable(draggableButtonRef, {
  initialValue: { x: 0, y: 0 }, // Will be set in onMounted
  preventDefault: true,
  onStart: () => {
    isDraggingActive.value = true;
    dragStartPos.x = x.value;
    dragStartPos.y = y.value;
  },
  onEnd: () => {
    isDraggingActive.value = false;
    // Check if moved significantly
    const dx = Math.abs(x.value - dragStartPos.x);
    const dy = Math.abs(y.value - dragStartPos.y);
    if (dx > 5 || dy > 5) {
      preventClick.value = true;
      // Reset after a short delay to allow click event to be ignored
      setTimeout(() => {
        preventClick.value = false;
      }, 100);
    }

    snapToEdge();
  },
});

const dragStartPos = reactive({ x: 0, y: 0 });
const preventClick = ref(false);

const dragJsStyles = computed(() => {
  // Combine the drag styles with any other necessary styles
  // We prioritize the drag position
  if (isDraggingActive.value || isInitialized.value) {
    return {
      left: `${x.value}px`,
      top: `${y.value}px`,
      // Override the default bottom/right/left classes
      bottom: "auto",
      right: "auto",
      position: "fixed",
      touchAction: "none",
      zIndex: "2147483647", // Max z-index
    };
  }
  return {
    bottom: `${props.buttonBottomOffset + 24}px`,
  };
});

const isInitialized = ref(false);

// Measured/expected button footprint, used for side + window anchoring math
const buttonSizePx = computed(() => (windowWidth.value <= 768 ? 70 : 96));

// Which screen edge the button currently lives on (drives tooltip + window side)
const currentSide = computed(() => {
  if (!isInitialized.value) return props.position;
  return x.value + buttonSizePx.value / 2 < windowWidth.value / 2
    ? "left"
    : "right";
});

// Whether the tooltip should sit above or below the button so it stays visible
const tooltipVertical = computed(() => {
  if (!isInitialized.value) return "top";
  return y.value < 100 ? "bottom" : "top";
});

const snapToEdge = () => {
  if (!draggableButtonRef.value) return;

  const buttonRect = draggableButtonRef.value.getBoundingClientRect();
  const buttonWidth = buttonRect.width;
  const padding = 24; // Margin from edge

  const midpoint = windowWidth.value / 2;
  let targetX;

  // Snap to Left or Right
  if (x.value + buttonWidth / 2 < midpoint) {
    targetX = padding;
  } else {
    targetX = windowWidth.value - buttonWidth - padding;
  }

  // Constrain Y
  let targetY = y.value;
  const buttonHeight = buttonRect.height;
  if (targetY < padding) targetY = padding;
  if (targetY > windowHeight.value - buttonHeight - padding) {
    targetY = windowHeight.value - buttonHeight - padding;
  }

  // Update position
  x.value = targetX;
  y.value = targetY;

  positionChatWindow();
};

// Chat window anchoring to the button's current position
const chatWindowRef = ref(null);
const chatWindowStyle = ref({});
const ESTIMATED_WINDOW_WIDTH = 400;

const computeWindowStyle = (measuredWidth, measuredHeight) => {
  const padding = windowWidth.value <= 768 ? 16 : 24;
  const winW = windowWidth.value;
  const winH = windowHeight.value;
  const bw = buttonSizePx.value;
  const wW =
    measuredWidth || Math.min(ESTIMATED_WINDOW_WIDTH, winW - padding * 2);
  const wH = measuredHeight || 0;

  const style = {
    position: "fixed",
    left: "auto",
    right: "auto",
    top: "auto",
    bottom: "auto",
  };

  // Horizontal: align the window to the same edge as the button, clamped
  if (currentSide.value === "left") {
    const left = Math.max(padding, Math.min(x.value, winW - wW - padding));
    style.left = `${left}px`;
  } else {
    const right = Math.max(
      padding,
      Math.min(winW - (x.value + bw), winW - wW - padding)
    );
    style.right = `${right}px`;
  }

  // Vertical: anchor the window bottom to the button bottom, clamp into view
  let bottom = winH - (y.value + bw);
  if (wH) bottom = Math.min(bottom, winH - wH - padding);
  bottom = Math.max(bottom, padding);
  style.bottom = `${bottom}px`;

  style.transformOrigin = `${currentSide.value === "left" ? "left" : "right"} bottom`;

  return style;
};

const positionChatWindow = () => {
  if (!isInitialized.value || !isExpanded.value) return;
  // Set an immediate estimate so the window opens on the correct side,
  // then refine using the real measured size after it renders.
  chatWindowStyle.value = computeWindowStyle();
  nextTick(() => {
    const el = chatWindowRef.value;
    if (!el) return;
    chatWindowStyle.value = computeWindowStyle(el.offsetWidth, el.offsetHeight);
  });
};

// Recalculate snap + window position on resize
watch([windowWidth, windowHeight], () => {
  if (!isInitialized.value) return;
  snapToEdge();
  positionChatWindow();
});

// Use reactive data from store
const messages = computed(() => chatStore.getMessages);
const isLoading = computed(() => chatStore.getIsLoading);
const sessionId = computed(() => chatStore.getSessionId);

const toggleChatbot = () => {
  if (preventClick.value) return;

  if (isExpanded.value) {
    showButton.value = false;
    setTimeout(() => {
      isExpanded.value = false;
      showTooltipNow.value = false;
      setTimeout(() => {
        showButton.value = true;
      }, 200);
    }, 50);
  } else {
    showButton.value = false;
    showTooltipNow.value = false;
    setTimeout(() => {
      isExpanded.value = true;
      positionChatWindow();
      // Scroll to bottom when opening
      scrollToBottom();
    }, 50);
  }
};

const showInitialTooltip = () => {
  showTooltipNow.value = true;
  tooltipTimer.value = setTimeout(() => {
    if (!isHovered.value) {
      showTooltipNow.value = false;
    }
  }, props.tooltipDuration);
};

const handleMouseEnter = () => {
  isHovered.value = true;
  if (props.showTooltip && !isExpanded.value) {
    showTooltipNow.value = true;
  }
  if (tooltipTimer.value) {
    clearTimeout(tooltipTimer.value);
  }
};

const handleMouseLeave = () => {
  isHovered.value = false;
  if (!isExpanded.value) {
    showTooltipNow.value = false;
  }
};

const getBotResponse = async (userMessage) => {
  try {
    chatStore.setLoading(true);
    hasReceivedFirstChunk.value = false;

    const location = localeStore.currentLocale;

    let accumulatedText = "";

    // Use streaming service
    await ChatbotService.sendMessageStreaming({
      query: userMessage,
      sessionId: sessionId.value,
      location,
      pageId: props.pageName,
      activityName: props.activityName,
      onChunk: (chunk) => {
        // Handle the chunk - it could be a string or JSON object
        let chunkText = "";

        if (typeof chunk === "string") {
          chunkText = chunk;
        } else if (typeof chunk === "object") {
          // If the chunk is an object, extract the text/data field
          chunkText =
            chunk.data || chunk.text || chunk.content || JSON.stringify(chunk);
        }

        // Accumulate the text (preserving newlines and whitespace)
        if (chunkText) {
          // On first chunk, add the bot message and hide loading indicator
          if (!hasReceivedFirstChunk.value) {
            hasReceivedFirstChunk.value = true;
            chatStore.addBotMessage("", "");

            // Mark the message as streaming
            const messages = chatStore.getMessages;
            if (messages.length > 0) {
              const lastMessage = messages[messages.length - 1];
              if (lastMessage.type === "bot") {
                lastMessage.isStreaming = true;
              }
            }
          }

          accumulatedText += chunkText;

          // Parse markdown to HTML for the accumulated text
          let htmlContent = marked.parse(accumulatedText);

          // Process item ID links to convert [ITEM_ID=XXX] to clickable buttons
          htmlContent = processItemIdLinks(htmlContent);

          // Update the last bot message with the accumulated content
          const messages = chatStore.getMessages;
          if (messages.length > 0) {
            const lastMessage = messages[messages.length - 1];
            if (lastMessage.type === "bot") {
              lastMessage.text = accumulatedText;
              lastMessage.html = htmlContent;
              lastMessage.isStreaming = true;
            }
          }

          // Scroll to bottom after each chunk
          scrollToBottom();
        }
      },
    });

    // Mark streaming as complete
    const messages = chatStore.getMessages;
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.type === "bot") {
        lastMessage.isStreaming = false;
      }
    }

    // Return the final accumulated response
    let finalHtml = marked.parse(accumulatedText);
    finalHtml = processItemIdLinks(finalHtml);

    return {
      text: accumulatedText,
      html: finalHtml,
    };
  } catch (error) {
    console.error("Error sending message to chatbot:", error);
    const errorText =
      "I'm sorry, there was an error processing your request. Please try again.";

    // Add error message if no bot message was created yet
    if (!hasReceivedFirstChunk.value) {
      chatStore.addBotMessage(errorText, errorText);
    } else {
      // Update the last bot message with error
      const messages = chatStore.getMessages;
      if (messages.length > 0) {
        const lastMessage = messages[messages.length - 1];
        if (lastMessage.type === "bot") {
          lastMessage.text = errorText;
          lastMessage.html = errorText;
          lastMessage.isStreaming = false;
        }
      }
    }

    return {
      text: errorText,
      html: errorText,
    };
  } finally {
    chatStore.setLoading(false);
  }
};

const processItemIdLinks = (htmlContent) => {
  // Pattern to match <strong>text</strong> [ITEM_ID=XXX] in HTML (after markdown parsing)
  // Keep the bold text and replace only [ITEM_ID=XXX] with the button
  const patternWithBoldHtml = /(<strong>[^<]*<\/strong>)\s*\[ITEM_ID=(\d+)\]/g;
  htmlContent = htmlContent.replace(
    patternWithBoldHtml,
    (match, boldText, itemId) => {
      const linkUrl = `/makercart/catalog?item=${itemId}`;
      return `${boldText} <a href="${linkUrl}" target="_blank" class="chatbot-activity-link" data-item-id="${itemId}">View Activity</a>`;
    }
  );

  // Handle standalone [ITEM_ID=XXX] patterns
  const itemIdPattern = /\[ITEM_ID=(\d+)\]/g;
  htmlContent = htmlContent.replace(itemIdPattern, (match, itemId) => {
    const linkUrl = `/makercart/catalog?item=${itemId}`;
    return `<a href="${linkUrl}" target="_blank" class="chatbot-activity-link" data-item-id="${itemId}">View Activity</a>`;
  });

  return htmlContent;
};

const handleActivityLinkClick = (event) => {
  const link = event.target.closest(".chatbot-activity-link");
  if (link) {
    const target = link.getAttribute("target");
    // If target="_blank", let the browser handle it (open in new tab)
    if (target === "_blank") {
      return; // Let default behavior happen
    }
    // Otherwise, use Vue Router for same-tab navigation
    event.preventDefault();
    const href = link.getAttribute("href");
    if (href) {
      router.push(href);
    }
  }
};

const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  });
};

const sendMessage = async (text) => {
  // Add user message to store
  chatStore.addUserMessage(text);
  scrollToBottom();

  // getBotResponse now handles adding and updating the bot message internally
  await getBotResponse(text);
  scrollToBottom();
};

const sendUserMessage = () => {
  if (inputMessage.value.trim() && !isLoading.value) {
    sendMessage(inputMessage.value);
    inputMessage.value = "";
  }
};

const fetchQuestions = async () => {
  if (!props.pageName) {
    return;
  }

  try {
    isLoadingQuestions.value = true;
    const response = await ChatbotService.getQuestions(props.pageName);
    if (response?.success && response?.data) {
      questions.value = response.data;
    } else {
      questions.value = [];
    }
  } catch (error) {
    console.error("Error fetching questions:", error);
    questions.value = [];
  } finally {
    isLoadingQuestions.value = false;
  }
};

// Watch for new messages and scroll to bottom
watch(
  () => messages.value.length,
  () => {
    if (isExpanded.value) {
      positionChatWindow();
      scrollToBottom();
    }
  },
  { flush: "post" }
);

// Watch for chatbot expansion to scroll to bottom
watch(
  () => isExpanded.value,
  (newValue) => {
    if (newValue) {
      positionChatWindow();
      nextTick(() => {
        scrollToBottom();
      });
    }
  }
);

onMounted(() => {
  if (props.autoShow) {
    setTimeout(() => {
      isVisible.value = true;
      if (props.showTooltip) {
        showInitialTooltip();
      }
    }, props.delay);
  } else {
    isVisible.value = true;
  }

  fetchQuestions();
});

onBeforeUnmount(() => {
  if (tooltipTimer.value) {
    clearTimeout(tooltipTimer.value);
  }
});

// Initialize position on mount
onMounted(async () => {
  // Give it a moment to render so we get correct dimensions
  await nextTick();

  if (!draggableButtonRef.value) return;

  const rect = draggableButtonRef.value.getBoundingClientRect();
  const buttonSize = rect.width || buttonSizePx.value;
  const padding = 24;
  const bottomOffset = props.buttonBottomOffset + 24; // from bottom

  // Calculate generic initial position based on props
  // We want to translate "bottom: 24px, right: 24px" into "left: X, top: Y"
  if (props.position === "left") {
    x.value = padding;
  } else {
    x.value = windowWidth.value - buttonSize - padding;
  }
  y.value = windowHeight.value - bottomOffset - buttonSize;

  isInitialized.value = true;
});

// Watch for pageName changes
watch(
  () => props.pageName,
  (newPageName) => {
    if (newPageName) {
      fetchQuestions();
    }
  },
  { immediate: false }
);
</script>

<style scoped>
.chatbot-assistant {
  position: fixed;
  bottom: 24px;
  z-index: var(--chatbot-assistant-z-index) !important;
  opacity: 0;
  transform: translateY(20px) scale(0.8);
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.chatbot-assistant--left {
  left: 0px;
}

.chatbot-assistant--right {
  right: 0;
}

.chatbot-assistant--visible {
  opacity: 1;
  /* changed from scale(1) to none to remove stacking context, allowing fixed children to be relative to viewport */
  transform: none;
}

.chatbot-button-container {
  position: fixed;
  bottom: 24px;
  z-index: var(--chatbot-assistant-z-index) !important;
}

.chatbot-assistant--left .chatbot-button-container {
  left: 24px;
}

.chatbot-assistant--right .chatbot-button-container {
  right: 24px;
}

.chatbot-button {
  position: relative;
  width: 96px;
  height: 96px;
  background: #b0ddcd;
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow:
    0px 6px 10px 4px rgba(0, 0, 0, 0.15),
    0px 2px 3px 0px rgba(0, 0, 0, 0.1);
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  overflow: hidden;
}

.chatbot-button:hover {
  transform: translateY(-2px);
  box-shadow:
    0px 8px 15px 6px rgba(0, 0, 0, 0.2),
    0px 4px 6px 0px rgba(0, 0, 0, 0.15);
}

.chatbot-button:active {
  transform: translateY(0);
  box-shadow:
    0px 4px 8px 3px rgba(0, 0, 0, 0.15),
    0px 2px 3px 0px rgba(0, 0, 0, 0.1);
}

.chatbot-button__icon {
  width: 60px;
  height: 60px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chatbot-button__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 50% 50%;
  transition: transform 0.3s ease;
}

.chatbot-button:hover .chatbot-button__image {
  transform: scale(1.1);
}

/* Button Animation */
.button-fade-enter-active {
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.button-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.button-fade-enter-from {
  opacity: 0;
  transform: scale(0.8);
}

.button-fade-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

.chatbot-tooltip {
  position: absolute;
  pointer-events: auto;
}

.chatbot-tooltip--left {
  left: 0;
}

.chatbot-tooltip--right {
  right: 0;
}

.chatbot-tooltip--top {
  top: -75px;
  bottom: auto;
}

.chatbot-tooltip--bottom {
  top: calc(100% + 12px);
  bottom: auto;
}

.chatbot-tooltip__content {
  background: #e6f4ef;
  padding: 12px 16px;
  border-radius: 8px;
  box-shadow:
    0px 4px 8px 3px rgba(0, 0, 0, 0.15),
    0px 1px 3px 0px rgba(0, 0, 0, 0.1);
  position: relative;
  max-width: 280px;
  min-width: 220px;
}

.chatbot-tooltip--left .chatbot-tooltip__content {
  text-align: left;
}

.chatbot-tooltip--right .chatbot-tooltip__content {
  text-align: right;
}

.chatbot-tooltip__text {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-small);
  line-height: 20px;
  color: #3b4648;
  margin: 0;
  letter-spacing: 0.035px;
}

.chatbot-tooltip__text:not(:last-child) {
  margin-bottom: 4px;
}

.chatbot-tooltip__arrow {
  width: 0;
  height: 0;
  position: absolute;
  bottom: -6px;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 6px solid #e6f4ef;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.chatbot-tooltip__arrow--left {
  left: 24px;
}

.chatbot-tooltip__arrow--right {
  right: 24px;
}

.chatbot-tooltip--bottom .chatbot-tooltip__arrow {
  top: -6px;
  bottom: auto;
  border-top: none;
  border-bottom: 6px solid #e6f4ef;
}

/* Chat Window */
.chatbot-window {
  position: fixed;
  bottom: 24px;
  width: 400px;
  height: auto;
  min-height: 200px;
  max-height: min(800px, calc(100vh - 150px));
  background: white;
  border-radius: 24px;
  box-shadow:
    0px 10px 30px 10px rgba(0, 0, 0, 0.15),
    0px 4px 8px 0px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition:
    height 0.3s ease-in-out,
    max-height 0.3s ease-in-out;
  z-index: var(--chatbot-assistant-z-index) !important;
}

.chatbot-assistant--left .chatbot-window {
  left: 24px;
}

.chatbot-assistant--right .chatbot-window {
  right: 24px;
}

.chatbot-window--with-messages {
  height: min(800px, calc(100vh - 150px));
}

.chatbot-header {
  background: linear-gradient(135deg, #7b88f4 0%, #8b96ff 100%);
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: white;
}

.chatbot-header__left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.chatbot-header__avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: white;
  padding: 4px;
  object-fit: cover;
}

.chatbot-header__info {
  flex: 1;
}

.chatbot-header__title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-big);
  line-height: 24px;
  margin: 0 0 4px 0;
}

.chatbot-header__subtitle {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-small);
  line-height: 18px;
  margin: 0;
  opacity: 0.95;
}

.chatbot-header__close {
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.chatbot-header__close:hover {
  background: rgba(255, 255, 255, 0.2);
}

.chatbot-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f8f9fc;
  position: relative;
}

.chatbot-messages {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.chatbot-message {
  display: flex;
  animation: messageSlideIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.chatbot-message--bot {
  justify-content: flex-start;
}

.chatbot-message--user {
  justify-content: flex-end;
}

.chatbot-message__content {
  max-width: 75%;
  padding: 12px 16px;
  border-radius: 16px;
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-small);
  line-height: 20px;
}

.chatbot-message--bot .chatbot-message__content {
  background: white;
  color: #3b4648;
  border-bottom-left-radius: 4px;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.08);
}

.chatbot-message--user .chatbot-message__content {
  background: #7b88f4;
  color: white;
  border-bottom-right-radius: 4px;
}

.chatbot-message__loading {
  background: white;
  color: #3b4648;
  border-bottom-left-radius: 4px;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 20px;
}

.loading-dots {
  display: flex;
  gap: 4px;
  align-items: center;
}

.loading-dots span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #7b88f4;
  animation: loadingDots 1.4s infinite ease-in-out both;
}

.loading-dots span:nth-child(1) {
  animation-delay: -0.32s;
}

.loading-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes loadingDots {
  0%,
  80%,
  100% {
    transform: scale(0.8);
    opacity: 0.5;
  }

  40% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes blink {
  0%,
  49% {
    opacity: 1;
  }

  50%,
  100% {
    opacity: 0;
  }
}

.chatbot-message__content--streaming {
  animation: streamingPulse 2s ease-in-out infinite;
}

@keyframes streamingPulse {
  0%,
  100% {
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.08);
  }

  50% {
    box-shadow: 0px 2px 8px rgba(123, 136, 244, 0.2);
  }
}

.chatbot-suggestions {
  padding: 20px;
  border-top: 1px solid #e5e7eb;
  background: white;
}

.chatbot-suggestions--sticky {
  position: sticky;
  top: 0;
  z-index: 10;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.chatbot-suggestions--initial {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.chatbot-suggestions__title {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 20px;
  color: #3b4648;
  margin: 0 0 12px 0;
  text-align: left;
}

.chatbot-suggestions__buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.chatbot-suggestion {
  display: block;
  background: white;
  border: 1.5px solid #e5e7eb;
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 8px;
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-small);
  line-height: 20px;
  color: #3b4648;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
}

.chatbot-suggestion:hover {
  border-color: #7b88f4;
  background: #f8f9fc;
  transform: translateY(-1px);
}

.chatbot-suggestion:active {
  transform: translateY(0);
}

.chatbot-suggestion:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
  background: #f3f4f6;
  border-color: #e5e7eb;
}

.chatbot-suggestions__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 12px;
}

.chatbot-suggestions__loading-text {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-small);
  line-height: 20px;
  color: #6c737f;
  margin: 0;
}

.chatbot-suggestions__empty {
  padding: 40px 20px;
  text-align: center;
}

.chatbot-suggestions__empty p {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-small);
  line-height: 20px;
  color: #9ca3af;
  margin: 0;
}

.chatbot-input {
  padding: 16px 20px;
  background: #f2f3fb;
  border-top: 1px solid #e5e7eb;
  display: flex;
  gap: 8px;
  align-items: center;
}

.chatbot-input__wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: 1.5px solid #6c737f;
  background-color: white;
  border-radius: 12px;
}

.chatbot-input__field {
  flex: 1;
  padding: 12px 0px;
  padding-left: 16px;
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-small);
  line-height: 20px;
  color: #3b4648;
  border: none;
  outline: none;
  transition: all 0.2s ease;
}

.chatbot-input__field::placeholder {
  color: #9ca3af;
}

.chatbot-input__send {
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.chatbot-input__send:hover {
  transform: scale(1.05);
}

.chatbot-input__send:active {
  transform: scale(0.95);
}

/* Tooltip Animation */
.tooltip-fade-enter-active {
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.tooltip-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.tooltip-fade-enter-from {
  opacity: 0;
  transform: translateY(10px) scale(0.95);
}

.tooltip-fade-leave-to {
  opacity: 0;
  transform: translateY(-5px) scale(0.98);
}

/* Chat Window Animation */
.chat-expand-enter-active {
  transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.chat-expand-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.chat-expand-enter-from {
  opacity: 0;
  transform: scale(0.8) translateY(30px);
}

.chat-expand-leave-to {
  opacity: 0;
  transform: scale(0.9) translateY(20px);
}

/* Message Animation */
.message-slide-enter-active {
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.message-slide-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

/* Suggestions Animation */
.suggestions-fade-enter-active {
  transition: all 0.4s ease 0.2s;
}

.suggestions-fade-leave-active {
  transition: all 0.3s ease;
}

.suggestions-fade-enter-from,
.suggestions-fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.chatbot-assistant:hover .chatbot-tooltip__content {
  background: #d8efe4;
  box-shadow:
    0px 6px 12px 4px rgba(0, 0, 0, 0.18),
    0px 2px 4px 0px rgba(0, 0, 0, 0.12);
}

.chatbot-assistant:hover .chatbot-tooltip__arrow {
  border-top-color: #d8efe4;
}

.chatbot-assistant:hover .chatbot-tooltip--bottom .chatbot-tooltip__arrow {
  border-bottom-color: #d8efe4;
}

@keyframes pulse {
  0% {
    box-shadow:
      0px 6px 10px 4px rgba(0, 0, 0, 0.15),
      0px 2px 3px 0px rgba(0, 0, 0, 0.1);
  }

  50% {
    box-shadow:
      0px 6px 10px 4px rgba(176, 221, 205, 0.4),
      0px 2px 3px 0px rgba(0, 0, 0, 0.1),
      0 0 0 4px rgba(176, 221, 205, 0.2);
  }

  100% {
    box-shadow:
      0px 6px 10px 4px rgba(0, 0, 0, 0.15),
      0px 2px 3px 0px rgba(0, 0, 0, 0.1);
  }
}

.chatbot-assistant--visible .chatbot-button {
  animation: pulse 2s ease-in-out infinite;
}

.chatbot-assistant:hover .chatbot-button {
  animation: none;
}

@media (max-width: 768px) {
  .chatbot-button-container {
    bottom: 16px;
  }

  .chatbot-assistant--left .chatbot-button-container {
    left: 16px;
  }

  .chatbot-assistant--right .chatbot-button-container {
    right: 16px;
  }

  .chatbot-button {
    width: 70px;
    height: 70px;
  }

  .chatbot-button__icon {
    width: 50px;
    height: 50px;
  }

  .chatbot-tooltip--top {
    top: -60px;
  }

  .chatbot-tooltip__content {
    max-width: 200px;
    min-width: 180px;
    padding: 8px 12px;
  }

  .chatbot-tooltip__text {
    font-size: 12px;
    line-height: 16px;
  }

  .chatbot-window {
    width: calc(100vw - 32px);
    height: auto;
    min-height: 200px;
    max-height: calc(100vh - 100px);
    max-width: 400px;
    bottom: 16px;
  }

  .chatbot-assistant--left .chatbot-window {
    left: 16px;
  }

  .chatbot-assistant--right .chatbot-window {
    right: 16px;
  }

  .chatbot-window--with-messages {
    height: calc(100vh - 100px);
    max-height: calc(100vh - 100px);
  }
}

@media (max-height: 700px) {
  .chatbot-window {
    max-height: calc(100vh - 100px);
    bottom: 16px;
  }

  .chatbot-window--with-messages {
    height: calc(100vh - 100px);
  }

  .chatbot-button-container {
    bottom: 16px;
  }

  .chatbot-assistant--left .chatbot-button-container {
    left: 16px;
  }

  .chatbot-assistant--right .chatbot-button-container {
    right: 16px;
  }

  .chatbot-assistant--left .chatbot-window {
    left: 16px;
  }

  .chatbot-assistant--right .chatbot-window {
    right: 16px;
  }
}

@media (max-height: 500px) {
  .chatbot-window {
    max-height: calc(100vh - 80px);
    min-height: 300px;
    bottom: 12px;
  }

  .chatbot-window--with-messages {
    height: calc(100vh - 80px);
  }

  .chatbot-button-container {
    bottom: 12px;
  }

  .chatbot-header {
    padding: 12px 16px;
  }

  .chatbot-header__avatar {
    width: 40px;
    height: 40px;
  }

  .chatbot-header__title {
    font-size: 18px;
    line-height: 22px;
  }

  .chatbot-header__subtitle {
    font-size: 12px;
    line-height: 16px;
  }

  .chatbot-messages {
    padding: 12px;
    gap: 8px;
  }

  .chatbot-suggestions {
    padding: 12px;
  }

  .chatbot-input {
    padding: 12px 16px;
  }
}
</style>

<style>
.chatbot-message__content--html {
  line-height: 1.6;
  color: #4c4ddc !important;
}

.chatbot-message__content--html h1,
.chatbot-message__content--html h2,
.chatbot-message__content--html h3,
.chatbot-message__content--html h4,
.chatbot-message__content--html h5,
.chatbot-message__content--html h6 {
  margin: 0 0 8px 0;
  font-weight: 600;
}

.chatbot-message__content--html h1 {
  font-size: 18px;
}

.chatbot-message__content--html h2 {
  font-size: var(--fs-medium);
}

.chatbot-message__content--html h3 {
  font-size: 15px;
}

.chatbot-message__content--html p {
  margin: 0 0 8px 0;
}

.chatbot-message__content--html p:last-child {
  margin-bottom: 0;
}

.chatbot-message__content--html ul,
.chatbot-message__content--html ol {
  margin: 8px 0;
  padding-left: 20px;
}

.chatbot-message__content--html li {
  margin: 4px 0;
}

.chatbot-message__content--html code {
  background: #f3f4f6;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: "Courier New", monospace;
  font-size: 13px;
  color: #e11d48;
}

.chatbot-message__content--html pre {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 6px;
  margin: 8px 0;
  overflow-x: auto;
  border: 1px solid #e5e7eb;
}

.chatbot-message__content--html pre code {
  background: none;
  padding: 0;
  color: #374151;
}

.chatbot-message__content--html blockquote {
  border-left: 4px solid #7b88f4;
  padding-left: 12px;
  margin: 8px 0;
  color: #6b7280;
  font-style: italic;
}

.chatbot-message__content--html strong {
  font-weight: 600;
}

.chatbot-message__content--html em {
  font-style: italic;
}

.chatbot-message__content--html a {
  color: #7b88f4;
  text-decoration: underline;
}

.chatbot-message__content--html a:hover {
  color: #6a77e8;
}

.chatbot-activity-link {
  display: inline-block;
  background: #7b88f4;
  color: white !important;
  padding: 8px 16px;
  border-radius: 8px;
  text-decoration: none !important;
  font-family: "League Spartan", sans-serif;
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  margin: 4px 0 4px 8px;
  transition: all 0.2s ease;
  box-shadow: 0px 2px 4px rgba(123, 136, 244, 0.3);
  border: none;
  cursor: pointer;
  vertical-align: middle;
}

.chatbot-activity-link:hover {
  background: #6a77e8;
  transform: translateY(-1px);
  box-shadow: 0px 4px 8px rgba(123, 136, 244, 0.4);
  color: white !important;
}

.chatbot-activity-link:active {
  transform: translateY(0);
  box-shadow: 0px 2px 4px rgba(123, 136, 244, 0.3);
}
</style>
